import { Typography } from "@/components/atoms";
import { PropertyCard, PropertyCardSkeleton } from "@/components/molecules";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { getUserFirstName } from "@/core/utils";
import { getResponsiveValue, isSmallScreen } from "@/core/utils/responsive";
import { fetchPropertyImagesForProjects } from "@/modules/home/hooks/usePropertyImages";
import {
  calculatePropertyOutstanding,
  getPropertyInstallments,
} from "@/modules/payments/utils/installmentUtils";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

/**
 * Map Property from store to PropertyCard props
 */
const mapPropertyToCard = (
  property: ReturnType<typeof useAppStore.getState>["properties"][0],
  unitInstallments: ReturnType<typeof useAppStore.getState>["unitInstallments"],
  t: (key: any, params?: any) => string,
  isRTL: boolean,
) => {
  // Calculate construction completed percentage (based on handover status)
  // Only show if handover is complete
  const constructionCompleted = property.handoverComplete ? "100%" : undefined;

  // Calculate installment paid percentage
  const installmentPaidPercent =
    property.totalPrice > 0
      ? Math.round((property.paidAmount / property.totalPrice) * 100)
      : 0;
  const installmentPaid = `${installmentPaidPercent}%`;

  // Get installments for this property to calculate status
  const installments = getPropertyInstallments(unitInstallments, property.id);
  const outstandingAmount = calculatePropertyOutstanding(installments);

  // Determine variant based on installment data
  let variant: "default" | "upcoming" | "noPending" = "default";
  let statusText = t("properties.noPendingDues");
  let statusCount: number | undefined;
  let statusLabel: string | undefined;

  let upcomingDueCount = 0;
  if (installments) {
    const overdueCount = installments.counts.overdue;
    upcomingDueCount = installments.counts.upcomingDue;

    if (overdueCount > 0) {
      // Has overdue payments - show red status (number in red, to the left of text in RTL)
      variant = "default";
      statusLabel =
        overdueCount === 1
          ? t("properties.missedPayment")
          : t("properties.missedPayments");
      statusCount = overdueCount;
      statusText = isRTL
        ? `${statusLabel} : ${overdueCount}`
        : `${overdueCount} ${statusLabel}`;
    } else if (upcomingDueCount > 0) {
      // Has payments due in 30 days - show yellow status
      variant = "upcoming";
      const translationKey =
        upcomingDueCount === 1
          ? "properties.paymentDueSoon"
          : "properties.paymentsDueSoon";
      statusLabel = t(translationKey).replace("{count}", "").trim();
      statusCount = upcomingDueCount;
      statusText = isRTL
        ? `${statusLabel} : ${upcomingDueCount}`
        : t(translationKey).replace("{count}", upcomingDueCount.toString());
    } else {
      // No overdue, no upcoming due (only future payments or no payments) - show green status
      variant = "noPending";
      statusText = t("properties.noPendingDues");
    }
  } else if (outstandingAmount > 0) {
    // Fallback to property outstanding amount if installments not available
    if (property.handoverComplete) {
      variant = "default";
      statusText = t("properties.missedPayments");
    } else {
      variant = "upcoming";
      statusText = t("properties.paymentsDueSoon");
    }
  } else {
    // No outstanding amount - show green status
    variant = "noPending";
    statusText = t("properties.noPendingDues");
  }

  // Create unit title from property data
  const unitTitle = property.unitNumber
    ? `${property.unitNumber}${property.bedrooms ? `, ${property.bedrooms}BHK` : ""}`
    : property.projectName;

  return {
    projectName: property.projectName,
    unitTitle,
    constructionCompleted,
    installmentPaid,
    variant,
    statusText,
    statusCount,
    statusLabel,
    images: [], // Will be populated from propertyImagesMap
    adhaUnitStatus:
      property.isADHAUnit && property.adhaUnitStatus?.trim()
        ? property.adhaUnitStatus.trim()
        : undefined,
  };
};

export function PropertiesScreenWeb() {
  useCatalogDynatraceView(DT_SCREEN.PROPERTIES, 'my_properties_viewed', true);
  // All hooks must be called unconditionally and in the same order every render
  const user = useAppStore((state) => state.auth.user);
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const profileLoading = useAppStore((state) => state.profileLoading);
  const unitInstallmentsLoading = useAppStore(
    (state) => state.unitInstallmentsLoading,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const isMobile = isSmallScreen();

  // Derived values (not hooks) - call after all hooks
  const firstName = getUserFirstName(user);
  const userEmail =
    user?.primary_email || user?.secondary_email || user?.tertiary_email || "";

  // State for property images mapped by project name
  const [propertyImagesMap, setPropertyImagesMap] = useState<
    Record<string, ImageSourcePropType[]>
  >({});
  const [imagesLoading, setImagesLoading] = useState(false);

  // Fetch property images when properties change
  useEffect(() => {
    const fetchImages = async () => {
      if (properties.length === 0) return;

      // Get unique project names
      const projectNames = Array.from(
        new Set(properties.map((p) => p.projectName).filter(Boolean)),
      ) as string[];

      if (projectNames.length === 0) return;

      setImagesLoading(true);
      try {
        const imagesMap = await fetchPropertyImagesForProjects(projectNames);
        setPropertyImagesMap(imagesMap);
      } catch (error) {
        // Error already logged in fetchPropertyImagesForProjects
      } finally {
        setImagesLoading(false);
      }
    };

    void fetchImages();
  }, [properties]);

  return (
    <WebAppLayout
      activeTab="properties"
      headerTitle={t("properties.myProperties")}
      showBackButton={false}
    >
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        {/* Main Content */}
        <View
          style={[
            styles.mainContent,
            {
              paddingBottom: getResponsiveValue(16, 32, 32),
            },
          ]}
        >
          {/* Content Area */}
          <ScrollView
            style={styles.contentArea}
            contentContainerStyle={styles.contentScroll}
          >
            {/* Content wrapper: same horizontal padding so heading aligns with cards */}
            <View style={styles.contentWrapper}>
              {/* Greeting Section - padding aligns heading with image inside property card */}
              <View
                style={[
                  styles.greetingSection,
                  { paddingLeft: isRTL ? 0 : 8, paddingRight: isRTL ? 8 : 0 },
                ]}
              >
                <View style={styles.greeting}>
                  <Typography
                    variant="title2"
                    color="primary"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {t("properties.hello")} {firstName}
                  </Typography>
                  <Typography
                    variant="body"
                    color="primary"
                    style={[
                      styles.subtitle,
                      { textAlign: isRTL ? "right" : "left" },
                    ]}
                  >
                    {t("properties.subtitle")}
                  </Typography>
                </View>
              </View>

              {/* Properties List (grid only on web) */}
              <View style={[styles.propertiesList, styles.gridContainer]}>
              {profileLoading ? (
                // Show skeletons for all properties when profile is loading
                Array.from({ length: 3 }).map((_, index) => (
                  <View key={`skeleton-${index}`} style={styles.gridItem}>
                    <PropertyCardSkeleton layout="grid" />
                  </View>
                ))
              ) : properties.length === 0 ? (
                <View style={styles.emptyState}>
                  <Typography
                    variant="body"
                    color="tertiary"
                    style={styles.emptyText}
                  >
                    {t("properties.noPropertiesFound")}
                  </Typography>
                </View>
              ) : (
                properties.map((property) => {
                  // Get installments for this property
                  const installments = getPropertyInstallments(
                    unitInstallments,
                    property.id,
                  );

                  // Show skeleton if unit installments are loading and this property doesn't have data yet
                  const showSkeleton = unitInstallmentsLoading && !installments;

                  if (showSkeleton) {
                    return (
                      <PropertyCardSkeleton key={property.id} layout="grid" />
                    );
                  }

                  const cardProps = mapPropertyToCard(
                    property,
                    unitInstallments,
                    t,
                    isRTL,
                  );

                  // Get images for this property from Contentful
                  const propertyImages = property.projectName
                    ? propertyImagesMap[property.projectName] || []
                    : [];

                  // Collect payment IDs based on variant:
                  // - For 'upcoming' variant: only select upcomingDue items (matching the count shown)
                  // - For 'default' variant: select overdue items
                  // - For 'noPending': no items to select
                  const paymentIdsToSelect: string[] = [];
                  if (installments) {
                    if (cardProps.variant === "upcoming") {
                      // Only select upcomingDue items (matching the count in statusText)
                      installments.upcomingDue.forEach((item) => {
                        paymentIdsToSelect.push(item.id);
                      });
                    } else if (cardProps.variant === "default") {
                      // Select overdue items
                      installments.overdue.forEach((item) => {
                        paymentIdsToSelect.push(item.id);
                      });
                    }
                    // Note: We don't select future items when clicking "Pay" from property card
                  }

                  return (
                    <View key={property.id} style={styles.gridItem}>
                      <PropertyCard
                        images={
                          propertyImages.length > 0
                            ? propertyImages
                            : cardProps.images
                        }
                        projectName={cardProps.projectName}
                        unitTitle={cardProps.unitTitle}
                        {...(cardProps.constructionCompleted && {
                          constructionCompleted:
                            cardProps.constructionCompleted,
                        })}
                        installmentPaid={cardProps.installmentPaid}
                        variant={cardProps.variant}
                        statusText={cardProps.statusText}
                        {...(cardProps.statusCount != null && {
                          statusCount: cardProps.statusCount,
                        })}
                        {...(cardProps.statusLabel != null && {
                          statusLabel: cardProps.statusLabel,
                        })}
                        layout="grid"
                        adhaUnitStatus={cardProps.adhaUnitStatus}
                        {...(property.isOnlinePaymentEnabled === true
                          ? {
                              onPrimaryActionPress: () => {
                                if (paymentIdsToSelect.length > 0) {
                                  router.push({
                                    pathname: "/(auth)/property-payments",
                                    params: {
                                      tab: "upcoming",
                                      selectedPaymentIds:
                                        paymentIdsToSelect.join(","),
                                    },
                                  } as any);
                                } else {
                                  router.push("/(auth)/(home)/payments" as any);
                                }
                              },
                            }
                          : {})}
                        onPress={() => {
                          router.push({
                            pathname: "/(auth)/property-details",
                            params: { propertyId: property.id },
                          } as any);
                        }}
                      />
                    </View>
                  );
                })
              )}
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    // Padding applied dynamically based on screen size
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 49,
  },
  headerTitle: {
    width: 235,
  },
  headerRight: {
    flexDirection: "row",
    gap: 24,
    alignItems: "center",
  },
  notificationButton: {
    width: 56,
    height: 56,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  profileButton: {
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  profileContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
  },
  profileInfo: {
    width: 168,
  },
  mainContent: {
    flex: 1,
    // Padding applied dynamically based on screen size
  },
  contentArea: {
    flex: 1,
  },
  contentScroll: {
    gap: 24,
  },
  contentWrapper: {
    width: "100%",
    gap: 24,
  },
  greetingSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
  },
  greeting: {
    flex: 1,
    gap: 8,
  },
  subtitle: {
    lineHeight: 20,
  },
  viewToggle: {
    flexDirection: "row",
    borderRadius: 8,
    borderWidth: 1,
    overflow: "hidden",
    width: 80,
    height: 32,
  },
  toggleButton: {
    width: 40,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  propertiesList: {
    gap: 16,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 24,
  },
  gridItem: {
    // Approximate Figma card width (378px) with responsive wrapping
    maxWidth: 378,
    flexBasis: "32%",
    flexGrow: 1,
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    textAlign: "center",
  },
});
